import { memo, useMemo } from 'react';
import { Focusable } from 'react-aria-components';

import { merge } from '@/components';
import { Flex, Text, TextProps, Tooltip, TooltipTrigger } from '@/components/core';
import { useImageSrc } from '@/hooks';
import { toProfileId, useGetProfile } from '@/hooks/api';
import { DivProps, ImgProps, OmitChildren } from '@/utils';

export type ProfileProps = OmitChildren<DivProps> & {
  profileId?: string | null;
  showEmail?: boolean | null;
  showName?: boolean | null;
  disableTooltip?: boolean;
  props?: { img?: ImgProps; text?: TextProps };
};

const IProfile = ({ profileId, showEmail, showName, className, disableTooltip, props, ...rest }: ProfileProps) => {
  const { data: profile } = useGetProfile(toProfileId(profileId));
  const { src } = useImageSrc({ source: profile?.pictureUrl });

  const { img: imgProps, text: textProps } = props ?? {};
  const { className: imgClassName, ...imgRest } = imgProps ?? {};

  const profileImg = useMemo(() => {
    if (src) {
      return (
        <img
          tabIndex={-1}
          role="button"
          className={merge('aspect-square rounded-full', imgClassName)}
          src={src}
          {...imgRest}
        />
      );
    } else {
      return <div className={merge('size-6 animate-pulse rounded-full bg-gray-200', imgClassName)} />;
    }
  }, [imgClassName, imgRest, src]);

  const visibleName = useMemo(() => {
    if (!profile) return '';
    return profile.givenName || profile.familyName ? `${profile.givenName} ${profile.familyName}` : profile.email;
  }, [profile]);

  if (!profile) return <div className={merge('size-6 animate-pulse rounded-full bg-gray-200', imgClassName)} />;

  return (
    <div className={merge('group/profile flex items-center gap-2', className)} {...rest}>
      {disableTooltip ? (
        <>{profileImg}</>
      ) : (
        <TooltipTrigger>
          <Tooltip>
            <div className="flex flex-col items-center">
              <Text>
                {profile.givenName} {profile.familyName}
              </Text>
              <Text>{profile.email}</Text>
            </div>
          </Tooltip>
          <Focusable>{profileImg}</Focusable>
        </TooltipTrigger>
      )}

      {showName || showEmail ? (
        <div className="flex flex-col items-center">
          {showName ? (
            <Text size="xs" {...textProps}>
              {visibleName}
            </Text>
          ) : null}
          {showEmail ? (
            <Text size="xs" {...textProps}>
              {profile.email}
            </Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export const Profile = memo(IProfile) as typeof IProfile;
